import { useMemo, useState } from "react";
import { IconInbox, IconSearch } from "@tabler/icons-react";
import {
  Badge,
  Card,
  CifraCargando,
  EmptyState,
  Group,
  Input,
  PaginadorTabla,
  Select,
  Stack,
  TablaSkeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { useServerPagination } from "@/lib/hooks/use-server-pagination";
import { formatFechaHora } from "@/shared/format/fecha";
import { useCatalogoNotificaciones, useEnvios } from "../../hooks/use-notificaciones";
import type { EstadoEnvio } from "../../services/notificaciones-service";
import { ETIQUETA_CANAL } from "../../utils/lienzo";
import { EnvioDetalleDialog } from "./EnvioDetalleDialog";
import { ESTADO_ENVIO, ESTADOS_FILTRABLES } from "./estado-envio";

interface Filtros {
  destinatario: string;
  tipoEvento: string;
  canal: string;
  estado: string;
  prueba: string;
}

const TODOS = "todos";

/** Bitácora paginada (P42) · todo filtro se resuelve en el servidor. */
export function BitacoraTab({ flujoId }: { flujoId?: string }) {
  const { data: catalogo } = useCatalogoNotificaciones();
  const p = useServerPagination<Filtros>({ destinatario: "", tipoEvento: TODOS, canal: TODOS, estado: TODOS, prueba: TODOS });
  const f = p.filtrosDebounced;
  const params = useMemo(
    () => ({
      flujoId,
      destinatario: f.destinatario.trim() || undefined,
      tipoEvento: f.tipoEvento === TODOS ? undefined : f.tipoEvento,
      canal: f.canal === TODOS ? undefined : f.canal,
      estado: f.estado === TODOS ? undefined : f.estado,
      esPrueba: f.prueba === TODOS ? undefined : f.prueba === "si",
      page: p.page,
      pageSize: p.pageSize,
    }),
    [f, flujoId, p.page, p.pageSize],
  );
  const { data, isLoading } = useEnvios(params);
  const [detalle, setDetalle] = useState<string | null>(null);
  const nombreEvento = useMemo(() => new Map(catalogo.eventos.map((e) => [e.codigo, e.nombre])), [catalogo]);
  const envios = data?.items ?? [];
  const total = data?.total ?? 0;
  const porEstado = data?.porEstado ?? {};

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group gap="sm" align="flex-end">
          <Input
            label="Destinatario"
            placeholder="Correo, URL o nombre…"
            leftSection={<IconSearch size={14} />}
            value={p.filtros.destinatario}
            onChange={(e) => p.patchFiltros({ destinatario: e.target.value })}
            wrapperClassName="w-64"
          />
          <Select
            label="Evento"
            searchable
            value={p.filtros.tipoEvento}
            onValueChange={(v) => p.patchFiltros({ tipoEvento: v })}
            options={[{ value: TODOS, label: "Todos" }, ...catalogo.eventos.map((e) => ({ value: e.codigo, label: e.nombre }))]}
            wrapperClassName="w-64"
          />
          <Select
            label="Canal"
            value={p.filtros.canal}
            onValueChange={(v) => p.patchFiltros({ canal: v })}
            options={[{ value: TODOS, label: "Todos" }, ...catalogo.canales.map((c) => ({ value: c, label: ETIQUETA_CANAL[c] }))]}
            wrapperClassName="w-48"
          />
          <Select
            label="Pruebas"
            value={p.filtros.prueba}
            onValueChange={(v) => p.patchFiltros({ prueba: v })}
            options={[
              { value: TODOS, label: "Todas" },
              { value: "no", label: "Solo reales" },
              { value: "si", label: "Solo pruebas" },
            ]}
            wrapperClassName="w-40"
          />
        </Group>

        <Group gap="xs">
          {[TODOS, ...ESTADOS_FILTRABLES].map((e) => {
            const activo = p.filtros.estado === e;
            // Sin datos aún (primera carga) · barra en vez de «· 0».
            const cantidad = !data ? undefined : e === TODOS ? Object.values(porEstado).reduce((a, b) => a + b, 0) : porEstado[e] ?? 0;
            return (
              <button
                key={e}
                type="button"
                onClick={() => p.patchFiltros({ estado: e })}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  activo ? "border-multiva-600 bg-multiva-600 text-white" : "border-border hover:bg-muted",
                )}
              >
                {e === TODOS ? "Todos" : ESTADO_ENVIO[e as EstadoEnvio].etiqueta} · <CifraCargando valor={cantidad} ancho={14} />
              </button>
            );
          })}
        </Group>

        {!isLoading && total === 0 ? (
          <EmptyState icon={IconInbox} title="Sin envíos" description="Aquí aparece cada notificación que el motor genera o envía." tone={p.filtrosActivos > 0 ? "filter" : "default"} />
        ) : (
          <>
            <Table>
              <Thead>
                <Tr>
                  <Th>Fecha</Th>
                  <Th>Evento</Th>
                  <Th>Notificación</Th>
                  <Th>Destinatario</Th>
                  <Th>Estado</Th>
                  <Th align="center">Intentos</Th>
                </Tr>
              </Thead>
              {isLoading ? (
                <TablaSkeleton dentroDeTabla columnas={[{}, {}, { lineas: 2 }, {}, {}, { align: "center" }]} filas={p.pageSize} />
              ) : (
                <Tbody>
                  {envios.map((e) => (
                    <Tr key={e.id} className="cursor-pointer" onClick={() => setDetalle(e.id)}>
                      <Td><Text size="sm">{formatFechaHora(e.creadoAt)}</Text></Td>
                      <Td><Text size="sm">{nombreEvento.get(e.tipoEvento) ?? e.tipoEvento}</Text></Td>
                      <Td>
                        <Text size="sm">{e.notificacionNombre}</Text>
                        <Text size="xs" c="dimmed">{ETIQUETA_CANAL[e.canal] ?? e.canal}{e.esPrueba ? " · prueba" : ""}</Text>
                      </Td>
                      <Td className="max-w-64">
                        <Text size="sm" truncate>{e.destinatarioNombre ?? e.destinatario}</Text>
                        {e.destinatarioNombre ? <Text size="xs" c="dimmed" truncate>{e.destinatario}</Text> : null}
                      </Td>
                      <Td className="max-w-72">
                        <Badge color={ESTADO_ENVIO[e.estado]?.color ?? "slate"}>{ESTADO_ENVIO[e.estado]?.etiqueta ?? e.estado}</Badge>
                        {e.ultimoError || e.motivoOmision ? (
                          <Text size="xs" c="dimmed" truncate title={e.ultimoError ?? e.motivoOmision ?? undefined}>
                            {e.ultimoError ?? e.motivoOmision}
                          </Text>
                        ) : null}
                      </Td>
                      <Td align="center">{e.intentos}</Td>
                    </Tr>
                  ))}
                </Tbody>
              )}
            </Table>
            <PaginadorTabla cargando={isLoading} total={total} page={p.page} pageSize={p.pageSize} onPageChange={p.setPage} sustantivo="envíos" />
          </>
        )}
      </Stack>
      <EnvioDetalleDialog envioId={detalle} onCerrar={() => setDetalle(null)} />
    </Card>
  );
}
